// Comprime un video ya seleccionado (galería/archivo) reencodeándolo en el
// navegador con canvas + MediaRecorder. NO abre la cámara: reproduce el archivo
// en un <video> oculto, lo dibuja a menor resolución en un <canvas> y graba ese
// canvas (más el audio original) a un bitrate controlado.
//
// Si el navegador no soporta MediaRecorder/captureStream (p. ej. Safari/iOS
// antiguo), devuelve el archivo original con `compressed: false` para que el
// llamador aplique su validación de tamaño y decida.

export interface CompressVideoOptions {
  maxHeight?: number; // altura máxima de salida (mantiene aspecto). Default 720
  videoBitsPerSecond?: number; // bitrate objetivo. Default 1.2 Mbps
  fps?: number; // cuadros por segundo de salida. Default 24
  onProgress?: (ratio: number) => void; // 0..1 según avance de la reproducción
}

export interface CompressVideoResult {
  file: File;
  compressed: boolean; // false = se devolvió el original (fallback)
}

export const canCompressVideo = (): boolean =>
  typeof MediaRecorder !== "undefined" &&
  typeof (HTMLCanvasElement.prototype as any).captureStream === "function";

const pickMimeType = (): string | null => {
  const candidates = [
    "video/mp4;codecs=h264,aac",
    "video/mp4",
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  for (const candidate of candidates) {
    if (
      typeof MediaRecorder.isTypeSupported === "function" &&
      MediaRecorder.isTypeSupported(candidate)
    ) {
      return candidate;
    }
  }
  return null;
};

export const compressVideo = (
  file: File,
  options: CompressVideoOptions = {},
): Promise<CompressVideoResult> => {
  const {
    maxHeight = 720,
    videoBitsPerSecond = 1_200_000,
    fps = 24,
    onProgress,
  } = options;

  return new Promise((resolve) => {
    const fallback = () => resolve({ file, compressed: false });

    if (!canCompressVideo()) return fallback();
    const mimeType = pickMimeType();
    if (!mimeType) return fallback();
    
    console.log(`[compressVideo] Codec seleccionado: ${mimeType}`);

    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.muted = true; // necesario para poder reproducir sin interacción
    video.playsInline = true;
    video.src = url;

    let rafId = 0;
    const cleanup = () => {
      cancelAnimationFrame(rafId);
      URL.revokeObjectURL(url);
    };

    video.onerror = () => {
      cleanup();
      fallback();
    };

    video.onloadedmetadata = () => {
      const scale = Math.min(1, maxHeight / (video.videoHeight || maxHeight));
      const width = Math.max(2, Math.round((video.videoWidth || 0) * scale));
      const height = Math.max(2, Math.round((video.videoHeight || 0) * scale));
      const duration = video.duration;

      console.log(`[compressVideo] Metadata del video cargada:`);
      console.log(`[compressVideo] - Resolución original: ${video.videoWidth}x${video.videoHeight}`);
      console.log(`[compressVideo] - Resolución de salida: ${width}x${height}`);
      console.log(`[compressVideo] - Duración: ${duration.toFixed(2)}s`);
      console.log(`[compressVideo] - FPS objetivo: ${fps}`);
      console.log(`[compressVideo] - Bitrate objetivo: ${(videoBitsPerSecond / 1_000_000).toFixed(1)} Mbps`);

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        cleanup();
        return fallback();
      }

      const canvasStream = (canvas as any).captureStream(fps) as MediaStream;
      let outputStream = canvasStream;

      // Intentar conservar el audio original.
      const mediaCapture =
        (video as any).captureStream || (video as any).mozCaptureStream;
      if (mediaCapture) {
        try {
          const srcStream: MediaStream = mediaCapture.call(video);
          const audioTrack = srcStream.getAudioTracks?.()[0];
          if (audioTrack) {
            outputStream = new MediaStream([
              canvasStream.getVideoTracks()[0],
              audioTrack,
            ]);
          }
        } catch {
          // sin audio: se graba solo el video
        }
      }

      let recorder: MediaRecorder;
      try {
        recorder = new MediaRecorder(outputStream, {
          mimeType,
          videoBitsPerSecond,
        });
      } catch {
        cleanup();
        return fallback();
      }

      const chunks: BlobPart[] = [];
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunks.push(event.data);
      };
      recorder.onstop = () => {
        cleanup();
        const type = mimeType.split(";")[0];
        const blob = new Blob(chunks, { type });
        const ext = type.includes("mp4") ? "mp4" : "webm";
        const baseName = file.name.replace(/\.[^.]+$/, "");
        const output = new File([blob], `${baseName}.${ext}`, { type });
        
        console.log(`[compressVideo] Grabación finalizada`);
        console.log(`[compressVideo] - Tamaño entrada: ${(file.size / 1_000_000).toFixed(2)} MB`);
        console.log(`[compressVideo] - Tamaño salida: ${(output.size / 1_000_000).toFixed(2)} MB`);
        console.log(`[compressVideo] - ¿Más pequeño?: ${output.size < file.size ? 'Sí' : 'No'}`);
        
        // Si por alguna razón el resultado no es más pequeño, conservar original.
        resolve(
          output.size > 0 && output.size < file.size
            ? { file: output, compressed: true }
            : { file, compressed: false },
        );
      };

      const draw = () => {
        ctx.drawImage(video, 0, 0, width, height);
        if (duration && onProgress) {
          onProgress(Math.min(1, video.currentTime / duration));
        }
        rafId = requestAnimationFrame(draw);
      };

      video.onended = () => {
        cancelAnimationFrame(rafId);
        if (recorder.state !== "inactive") recorder.stop();
      };

      recorder.start(1000);
      console.log(`[compressVideo] Iniciando reproducción y grabación...`);
      video
        .play()
        .then(() => draw())
        .catch(() => {
          cleanup();
          if (recorder.state !== "inactive") recorder.stop();
          fallback();
        });
    };
  });
};
